/**
 * LocalStorage keys for SEPA RTP form persistence
 */
const STORAGE_KEYS = {
  BENEFICIARY_NAME: 'sepa_rtp_name',
  IBAN: 'sepa_rtp_iban',
  BIC: 'sepa_rtp_bic',
  AMOUNT: 'sepa_rtp_amount'
} as const

/**
 * Stored beneficiary data (persisted between sessions)
 */
export interface StoredBeneficiaryData {
  name: string
  iban: string
  bic: string
  amount: string
}

/**
 * Check if localStorage is available
 */
function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch {
    return false
  }
}

/**
 * Load persisted beneficiary data from localStorage
 */
export function loadStoredData(): StoredBeneficiaryData {
  if (!isStorageAvailable()) {
    return { name: '', iban: '', bic: '', amount: '' }
  }

  return {
    name: localStorage.getItem(STORAGE_KEYS.BENEFICIARY_NAME) || '',
    iban: localStorage.getItem(STORAGE_KEYS.IBAN) || '',
    bic: localStorage.getItem(STORAGE_KEYS.BIC) || '',
    amount: localStorage.getItem(STORAGE_KEYS.AMOUNT) || ''
  }
}

/**
 * Save beneficiary data to localStorage
 */
export function saveStoredData(data: Partial<StoredBeneficiaryData>): void {
  if (!isStorageAvailable()) return

  if (data.name !== undefined) {
    localStorage.setItem(STORAGE_KEYS.BENEFICIARY_NAME, data.name)
  }
  if (data.iban !== undefined) {
    localStorage.setItem(STORAGE_KEYS.IBAN, data.iban)
  }
  if (data.bic !== undefined) {
    localStorage.setItem(STORAGE_KEYS.BIC, data.bic)
  }
  if (data.amount !== undefined) {
    localStorage.setItem(STORAGE_KEYS.AMOUNT, data.amount)
  }
}

/**
 * Clear all stored data
 */
export function clearStoredData(): void {
  if (!isStorageAvailable()) return

  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key)
  })
}

/**
 * Check if there is stored data
 */
export function hasStoredData(): boolean {
  if (!isStorageAvailable()) return false

  return !!(
    localStorage.getItem(STORAGE_KEYS.BENEFICIARY_NAME) ||
    localStorage.getItem(STORAGE_KEYS.IBAN)
  )
}
